// 営業日カレンダー（配送案内のフリーページ 5 店 9 ページ）。wuSiteResource2 の Shared/Calender.ts（AngularJS 1.x の
// CalenderApp / MainCtrl）の引き取り先（2026-09-24）。API も OppSites の /Api/SearchKey/CalcDelvDay から
// wuStorefront の /api/business-calendar へ移した（src/server/business-calendar/）。
//
// ページ本文は現行どおり（<div ng-app="CalenderApp" ng-controller="MainCtrl as c"> の中に
// select[ng-model="c.selectDay"] と div.calendar）。jQuery UI + datepicker-ja はページ側が読む。AngularJS は読まない。
//   - 起動時 jQuery('.calendar').datepicker({ numberOfMonths: 2 }) で今月・来月をインライン表示（現行どおり）
//   - 日付の選択はさせない（表示専用）。現行は td a の click に return false だったが、jQuery UI の selectDay は td 側に
//     付いていて先に走るので効いていなかった。ここは capture で止める（選択されると再描画されて塗りが消える）
//   - 納期 select の change で API を叩き、休日の日セルに .holiday、発送予定日に .shipping を付ける（現行どおり
//     a.ui-state-default に付ける。CSS は .ui-datepicker-calendar .holiday）。初回は未選択のまま叩いて休日だけ塗る
//   - 塗る先は td の data-year / data-month（jQuery UI が付ける）と応答の年月で突き合わせる。現行は「1 枚目の表 = 今月」
//     決め打ちで、月末をまたいで開きっぱなしだとずれていた。月送りしたら塗り直す（onChangeMonthYear）
// ★ 旧 Calender.js がまだ読まれているページ（angular.module('CalenderApp') が引ける）では何もしない。
//   旧が外れて angular.min.js だけ残っている間は ng-app 属性を外して angular の自動起動（モジュール無しエラー）を止める。
// ★ dansai の 2 ページは ng-app="CalenderApp" の div が 2 つある（2 つ目は文章だけ）。.calendar を持つ方だけ扱う。
// ★ つづりは「Calender」（ページ本文・旧ファイル名どおり）。hook 名と API は calendar。

import { resolveDevApiOverride } from '../shared/dev-api-override'

export const BUSINESS_CALENDAR_API = 'https://front.workup-system.com/api/business-calendar'
export const APP_SELECTOR = '[ng-app="CalenderApp"]'

export interface CalendarMonth {
  year: number
  month: number
  holidays: number[]
  shippingDay: number | null
}

/** 旧 Calender.js（AngularJS のモジュール CalenderApp）が同じページに居るか */
export function hasOldCalendarApp(win: { angular?: { module?: (name: string) => unknown } }): boolean {
  const ng = win.angular
  if (!ng || typeof ng.module !== 'function') return false
  try {
    ng.module('CalenderApp')
    return true
  } catch {
    return false // 未登録なら angular が投げる
  }
}

/** select の値 → dayNum。未選択（""）や数でなければ null（休日だけ塗る） */
export function readDayNum(value: string): number | null {
  if (value.trim() === '') return null
  const n = Number(value)
  return Number.isInteger(n) && n >= 0 ? n : null
}

export function apiUrl(dayNum: number | null, base: string = BUSINESS_CALENDAR_API): string {
  return dayNum === null ? base : `${base}?dayNum=${dayNum}`
}

/** 1 つの日セルに付ける class。応答に無い月は何も付けない */
export function cellClasses(year: number, month: number, day: number, months: CalendarMonth[]): string[] {
  const m = months.find((x) => x.year === year && x.month === month)
  if (!m) return []
  const out: string[] = []
  if (m.holidays.includes(day)) out.push('holiday')
  if (m.shippingDay === day) out.push('shipping')
  return out
}

/**
 * datepicker が描いた日セル（td[data-year][data-month] の .ui-state-default）に holiday / shipping を付け直す。
 * 戻り値は付けたセルの数（検証用）
 */
export function paintCalendar(container: Element, months: CalendarMonth[]): number {
  let painted = 0
  const cells = container.querySelectorAll<HTMLElement>('.ui-datepicker-calendar td[data-year][data-month]')
  for (const td of Array.from(cells)) {
    const cell = td.querySelector('.ui-state-default')
    if (!cell) continue
    const day = Number(cell.textContent)
    cell.classList.remove('holiday', 'shipping')
    const cls = cellClasses(Number(td.dataset.year), Number(td.dataset.month) + 1, day, months)
    if (cls.length === 0) continue
    cell.classList.add(...cls)
    painted++
  }
  return painted
}

/** 日付の選択を止める（表示専用）。jQuery UI のハンドラは td に付くので、その手前（capture）で止める */
export function blockDaySelect(calendar: Element): void {
  calendar.addEventListener(
    'click',
    (e) => {
      const t = e.target as Element | null
      if (t && typeof t.closest === 'function' && t.closest('.ui-datepicker-calendar td a')) {
        e.preventDefault()
        e.stopPropagation()
      }
    },
    true,
  )
}

interface JQueryLike {
  (el: Element): { datepicker: (opts: Record<string, unknown>) => unknown }
  fn?: { datepicker?: unknown }
}

/** ローカル検証（複製ページ + devserver）のための上書き。localhost / 127.0.0.1 だけ受ける（Issue #103） */
export function apiBase(search: string = location.search): string {
  return resolveDevApiOverride(search, 'wu_calendar_api', BUSINESS_CALENDAR_API)
}

export default function businessCalendar(): void {
  const win = window as unknown as Record<string, unknown>
  if (hasOldCalendarApp(win as { angular?: { module?: (name: string) => unknown } })) return

  const apps = Array.from(document.querySelectorAll(APP_SELECTOR))
  const root = apps.find((a) => a.querySelector('.calendar'))
  const calendar = root?.querySelector('.calendar')
  if (!root || !calendar) return
  for (const a of apps) a.removeAttribute('ng-app') // angular.min.js だけ残っていても自動起動させない

  const jq = win.jQuery as JQueryLike | undefined
  if (!jq || !jq.fn || typeof jq.fn.datepicker !== 'function') {
    console.warn('[wu] business-calendar: jQuery UI の datepicker がありません')
    return
  }
  const select = root.querySelector<HTMLSelectElement>('select[ng-model="c.selectDay"]')

  let months: CalendarMonth[] = []
  const repaint = (): void => {
    paintCalendar(calendar, months)
  }
  jq(calendar).datepicker({
    numberOfMonths: 2,
    // onChangeMonthYear は再描画の前に呼ばれるので、描き終わってから塗る
    onChangeMonthYear: () => {
      setTimeout(repaint, 0)
    },
  })
  blockDaySelect(calendar)

  let seq = 0
  const load = (): void => {
    const my = ++seq
    fetch(apiUrl(readDayNum(select?.value ?? ''), apiBase()), { cache: 'no-store' })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((json: unknown) => {
        if (my !== seq) return // 後から選び直した方を優先
        const m = (json as { months?: unknown } | null)?.months
        if (!Array.isArray(m)) return
        months = m as CalendarMonth[]
        repaint()
      })
      .catch((e) => {
        // 現行も取得失敗は無表示だった。カレンダーは素のまま残る
        console.warn('[wu] business-calendar: 営業日の取得に失敗しました', e)
      })
  }
  select?.addEventListener('change', load)
  load()
}
